import React, { useState, useRef } from 'react';
import { ArrowLeft, Github, ExternalLink, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import cloVideo from "@/assets/projects/clo3d/0106.mp4";
import cloCover from "@/assets/projects/clo3d/0426(3)-Cover.jpg";

const CLO3DGarmentPlugin: React.FC = () => {
  const [isVideoRevealed, setIsVideoRevealed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleReveal = () => {
    setIsVideoRevealed(true);
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log("Playback failed:", e));
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>

      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Software Project (Ongoing)</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">CLO3D Garment Plugin (AI-Powered 3D Design Assistant)</h1>
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">TypeScript</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Node.js</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">WebSocket</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">MCP / LLM APIs</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">TCP Sockets</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Zod Validation</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Project Video */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm bg-black">
            <div className="relative aspect-video flex items-center justify-center w-full h-full group">
              {!isVideoRevealed && (
                <div
                  className="absolute inset-0 z-20 flex items-center justify-center cursor-pointer overflow-hidden transition-all duration-300 group-hover:brightness-110"
                  onClick={handleReveal}
                >
                  <img src={cloCover} alt="Demo Cover" className="absolute inset-0 w-full h-full object-cover blur-[8px] scale-105 opacity-90" />
                  <div className="absolute inset-0 bg-black/30"></div>
                  <Button variant="secondary" className="relative z-30 bg-white hover:bg-gray-200 text-black border-0 font-bold rounded-full px-10 py-6 shadow-2xl tracking-widest uppercase text-lg">
                    Watch Demo
                  </Button>
                </div>
              )}
              <video
                ref={videoRef}
                src={cloVideo}
                className="w-full h-full object-contain"
                controls={isVideoRevealed}
                preload="metadata"
                autoPlay={isVideoRevealed}
                muted
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Developed an AI-powered integration that bridges Claude AI with CLO3D (professional 3D garment design software) via a WebSocket-based MCP (Model Context Protocol) server. This enables natural language-driven garment discovery, import, and design workflow automation. It effectively turns manual design queries into automated, streamlined commands directly injecting into a professional environment.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-foreground">How It Works</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The architecture comprises a WebSocket Bridge spanning over 1,000 lines of code, functioning as the core orchestration hub orchestrating tool invocations via streaming LLM pipelines linked with OpenRouter APIs. Beneath this hub lies an MCP server built on robust child process spawning, parsing dynamic tool modules dynamically. Finally, External Integrations map right to the CLO3D TCP port and custom file system scanners.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li><strong>Multi-Client Concurrency</strong> - Independent conversation histories per client</li>
                  <li><strong>Streaming Response Protocol</strong> - Real-time token streaming with tool call orchestration</li>
                  <li><strong>Process Isolation</strong> - Child process architecture with auto-recovery</li>
                  <li><strong>Type Safety</strong> - Strict TypeScript with Zod validation for all inputs</li>
                  <li><strong>Extensibility</strong> - Advanced plugin loader pattern enables easy tool expansion</li>
                  <li><strong>LLM Integration</strong> - Custom streaming context for multi-turn conversations</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Architecture</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Engineered using Node.js and TypeScript, orchestrating multiple processes through stdio channels and handling JSON-RPC 2.0 communication protocols. TCP socket clients are manually implemented for bridging local .NET CLO3D endpoints directly alongside a robust system prompting methodology providing instructions, tool syncing, and relevance scoring models to Claude.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start opacity-75 cursor-not-allowed" asChild disabled>
                    <div className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code (Private / Org)</span>
                    </div>
                  </Button>
                </div>
              </div>

              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">TS</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">TypeScript</p>
                      <p className="text-sm text-muted-foreground">Strict Mode / ES2022</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">N</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Node.js</p>
                      <p className="text-sm text-muted-foreground">Backend Runtime</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">WS</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">WebSockets</p>
                      <p className="text-sm text-muted-foreground">Real-time IO</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">M</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">MCP Standard</p>
                      <p className="text-sm text-muted-foreground">Model Context Protocol</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Impact</h3>
                <p className="text-muted-foreground mb-4">
                  Transformed CLO3D interaction workflows from strict manual directory search into intelligent prompt-based automations, saving numerous clicks per design import while demonstrating sophisticated backend orchestration and LLM protocol handling.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Hospital_Readmission_Prediction_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Hospital Readmission Prediction</h3>
              <p className="text-muted-foreground line-clamp-2">A meta-classifier ensemble system that predicts hospital readmission risk with high accuracy.</p>
            </Link>
            <Link to="/recipe-generator-ai" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">AI Recipe Generator</h3>
              <p className="text-muted-foreground line-clamp-2">An intelligent AI-powered platform for generating customized meal plans.</p>
            </Link>
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">Self-hosted cloud storage with TrueNAS and advanced routing.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CLO3DGarmentPlugin;
